import React, { useEffect } from "react";

const MaharishiInvite = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.12 }
    );
    document.querySelectorAll(".fade-in").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="invite-container">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=Inter:wght@300;400;500&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --bg:     #06100a;
          --card:   rgba(255,255,255,0.05);
          --border: rgba(255,255,255,0.10);
          --gold:   #d4a853;
          --sky:    #7dd3fc;
          --text:   #ffffff;
          --muted:  rgba(255,255,255,0.82);
          --white:  #ffffff;
        }

        .fade-in { opacity:0; transform:translateY(22px); transition:opacity .65s ease, transform .65s ease; }
        .fade-in.visible { opacity:1; transform:translateY(0); }

        /* ── WRAPPER ── */
        .invite-container {
          width: 100%;
          font-family: 'Inter', sans-serif;
          color: var(--text);
          position: relative;
          overflow-x: hidden;

          background:
            radial-gradient(ellipse 70% 38% at 50% -5%,  rgba(212, 169, 83, 0.39)  0%, transparent 65%),
            radial-gradient(ellipse 55% 45% at -5% 95%,  rgba(180,110,30,0.18)  0%, transparent 62%),
            radial-gradient(ellipse 50% 40% at 105% 55%, rgba(60,140,60,0.13)   0%, transparent 60%),
            radial-gradient(ellipse 45% 35% at 100% 0%,  rgba(30,90,120,0.18)   0%, transparent 58%),
            linear-gradient(160deg, #050e07 0%, #091408 25%, #0d1a0b 55%, #07120e 80%, #050e07 100%);
        }

        /* ── GRAIN TEXTURE OVERLAY ── */
        .invite-container::before {
          content: "";
          position: fixed;
          inset: 0;
          pointer-events: none;
          z-index: 0;
          opacity: 0.028;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E");
          background-size: 200px 200px;
        }

        /* ── VERTICAL DIVIDER LINES ── */
        .invite-container::after {
          content: "";
          position: fixed;
          inset: 0;
          pointer-events: none;
          z-index: 0;
          background-image:
            repeating-linear-gradient(
              90deg,
              transparent,
              transparent 119px,
              rgba(212,168,83,0.025) 120px
            );
        }

        .inv-hero, .inv-section, .inv-cta-wrap, hr.inv-hr {
          position: relative; z-index: 1;
        }

        /* ── HERO ── */
        .inv-hero {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 120px 24px 72px;
        }

        .inv-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(212,168,83,0.10);
          border: 1px solid rgba(212,168,83,0.30);
          color: var(--gold);
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          padding: 6px 18px;
          border-radius: 999px;
          margin-bottom: 30px;
        }

        .inv-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(58px, 11vw, 110px);
          font-weight: 700;
          color: var(--gold);
          line-height: 1;
          letter-spacing: 0.04em;
          margin-bottom: 10px;
          text-shadow: 0 0 80px rgba(212,168,83,0.30), 0 0 160px rgba(180,110,30,0.15);
        }

        .inv-fullform {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(14px, 1.8vw, 20px);
          /* FIX: explicit white with shadow so it doesn't blend into background */
          color: #ffffff;
          font-weight: 600;
          letter-spacing: 0.02em;
          margin-bottom: 30px;
          max-width: 560px;
          text-shadow: 0 1px 6px rgba(0,0,0,0.6);
        }

        .inv-divider {
          width: 52px; height: 2px;
          background: linear-gradient(90deg, transparent, var(--gold), transparent);
          margin: 0 auto 28px;
        }

        .inv-tagline {
          max-width: 560px;
          font-size: clamp(14px, 1.8vw, 17px);
          /* FIX: bumped from semi-transparent muted to solid off-white */
          color: #e8e8e8;
          line-height: 1.9;
          font-weight: 300;
          margin-bottom: 44px;
        }

        /* ── STAT STRIP ── */
        .inv-strip {
          display: flex;
          align-items: stretch;
          flex-wrap: wrap;
          justify-content: center;
          /* FIX: darkened background so text inside has more contrast */
          background: rgba(0,0,0,0.35);
          border: 1px solid rgba(212,168,83,0.25);
          border-radius: 18px;
          overflow: hidden;
          width: 100%;
          max-width: 680px;
          margin-bottom: 48px;
          box-shadow: inset 0 0 40px rgba(212,168,83,0.04), 0 4px 32px rgba(0,0,0,0.35);
        }
        .inv-strip-item {
          flex: 1; min-width: 120px;
          padding: 20px 16px;
          text-align: center;
          border-right: 1px solid rgba(212,168,83,0.15);
        }
        .inv-strip-item:last-child { border-right: none; }
        .inv-strip-num {
          display: block;
          font-size: 28px; font-weight: 700;
          color: var(--gold); line-height: 1; margin-bottom: 5px;
        }
        /* FIX: label was near-invisible white on transparent — changed to solid light green-white */
        .inv-strip-label {
          display: block;
          font-size: 10px; color: #c8d4c8;
          letter-spacing: 0.08em; text-transform: uppercase;
        }

        /* ── BUTTONS ── */
        .inv-actions { display:flex; gap:14px; flex-wrap:wrap; justify-content:center; margin-bottom:48px; }
        .inv-btn {
          padding: 13px 30px;
          font-family: 'Inter', sans-serif;
          font-size: 14px; font-weight: 500;
          border-radius: 999px; cursor: pointer; border: none;
          transition: transform .2s, box-shadow .2s;
          letter-spacing: 0.02em; text-decoration: none; display:inline-block;
        }
        .inv-btn-primary {
          background: linear-gradient(135deg, var(--gold), #b8862e);
          color: #060e18;
          box-shadow: 0 4px 20px rgba(212,168,83,0.35);
        }
        .inv-btn-primary:hover { transform:translateY(-3px); box-shadow:0 8px 28px rgba(212,168,83,0.5); }
        /* FIX: outline button had white text on transparent bg — added faint tint and stronger border */
        .inv-btn-outline {
          background: rgba(255,255,255,0.08);
          color: #ffffff;
          border: 1px solid rgba(255,255,255,0.30);
        }
        .inv-btn-outline:hover { background: rgba(255,255,255,0.14); transform:translateY(-2px); }

        /* ── SECTIONS ── */
        .inv-section {
          max-width: 1060px;
          margin: 0 auto;
          padding: 72px 24px;
        }
        .inv-eyebrow {
          font-size: 13px; letter-spacing: 0.18em;
          text-transform: uppercase; color: var(--gold);
          text-align: center; margin-bottom: 10px; font-weight: 500;
        }
        /* FIX: explicitly set white — was relying on inheritance which can silently fail */
        .inv-sec-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(26px, 3.5vw, 42px);
          color: #ffffff; font-weight: 700;
          text-align: center; margin-bottom: 12px; line-height: 1.15;
        }
        /* FIX: bumped from semi-transparent muted to solid off-white */
        .inv-sec-desc {
          font-size: 16px; color: #d8e4d8;
          line-height: 1.85; font-weight: 300;
          text-align: center;
          max-width: 640px; margin: 0 auto 48px;
        }

        hr.inv-hr {
          border: none;
          border-top: 1px solid rgba(212,168,83,0.12);
          margin: 0 24px;
        }

        /* ── VISION / MISSION 2-col ── */
        .vm-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }
        @media (max-width: 720px) { .vm-grid { grid-template-columns: 1fr; } }
        /* FIX: darkened card background for better text contrast */
        .vm-card {
          background: rgba(8,24,10,0.75);
          border: 1px solid rgba(212,168,83,0.20);
          border-radius: 18px;
          padding: 32px 28px;
          backdrop-filter: blur(6px);
          box-shadow: 0 2px 24px rgba(0,0,0,0.4);
        }
        .vm-card-label {
          font-size: 11px; letter-spacing: 0.18em;
          text-transform: uppercase; color: var(--gold);
          margin-bottom: 12px; display: block; font-weight: 600;
        }
        /* FIX: h3 had no color set — explicitly white */
        .vm-card h3 {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(20px, 2.2vw, 26px);
          color: #ffffff; font-weight: 700;
          margin-bottom: 12px; line-height: 1.2;
        }
        /* FIX: body text changed from semi-transparent muted to solid readable off-white */
        .vm-card p {
          font-size: 15px; color: #d4e4d4;
          line-height: 1.85; font-weight: 300;
        }

        /* ── WHY IT MATTERS (SDG pills) ── */
        .sdg-row {
          display: flex; flex-wrap: wrap;
          gap: 10px; justify-content: center;
          margin-bottom: 32px;
        }
        /* FIX: was white text on rgba(255,255,255,0.07) — nearly invisible.
           Changed to gold-tinted background with warm cream text */
        .sdg-pill {
          display: inline-flex; align-items: center; gap: 7px;
          background: rgba(212,168,83,0.12);
          border: 1px solid rgba(212,168,83,0.28);
          color: #f0d898;
          font-size: 12px; font-weight: 500;
          padding: 6px 14px; border-radius: 999px;
          letter-spacing: 0.03em;
        }
        /* FIX: bumped from semi-transparent muted to solid readable color */
        .matter-desc {
          max-width: 680px; margin: 0 auto;
          font-size: 15px; color: #d4e4d4;
          line-height: 1.9; text-align: center; font-weight: 300;
        }

        /* ── PARTNER TYPES ── */
        .partner-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 16px;
        }
        .partner-card {
          display: flex; align-items: flex-start; gap: 14px;
          background: rgba(15,35,15,0.6);
          border: 1px solid rgba(212,168,83,0.10);
          border-radius: 14px; padding: 20px 18px;
          transition: border-color .3s, transform .2s;
        }
        .partner-card:hover { border-color: rgba(74,222,128,.28); transform: translateY(-4px); }
        .p-icon { font-size: 22px; flex-shrink: 0; margin-top: 2px; }
        /* FIX: explicitly white */
        .partner-card h4 { font-size: 14px; font-weight: 500; color: #ffffff; margin-bottom: 4px; }
        .partner-card p { font-size: 13px; color: #c0d0c0; line-height: 1.65; }

        /* ── CTA BANNER ── */
        .inv-cta-wrap { padding: 0 24px 80px; }
        /* FIX: darkened CTA background for better text contrast */
        .inv-cta {
          max-width: 860px; margin: 0 auto;
          background: linear-gradient(135deg, rgba(8,28,12,0.85), rgba(10,35,15,0.75));
          border: 1px solid rgba(212,168,83,0.28);
          border-radius: 26px; padding: 60px 36px; text-align: center;
          box-shadow: 0 8px 48px rgba(0,0,0,0.5);
        }
        /* FIX: explicitly white */
        .inv-cta h2 {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(24px, 3.5vw, 40px);
          font-weight: 700; color: #ffffff; margin-bottom: 14px;
        }
        /* FIX: bumped from semi-transparent muted to solid readable color */
        .inv-cta p {
          font-size: 15px; color: #ccdacc;
          max-width: 520px; margin: 0 auto 32px;
          line-height: 1.8; font-weight: 300;
        }
        .inv-cta-btns { display:flex; gap:14px; justify-content:center; flex-wrap:wrap; }

        /* FIX: footnote was 0.75 opacity on already-muted color — changed to solid readable tint */
        .inv-footnote {
          font-size: 13px; color: #a8bca8;
          text-align: center; line-height: 1.75;
          max-width: 460px; margin: 32px auto 0;
        }
        .inv-footnote a { color: #d4e8d4; text-decoration: none; }
        .inv-footnote a:hover { text-decoration: underline; color: #ffffff; }

        @media (max-width: 640px) {
          .inv-hero { padding-top: 100px; }
          .inv-strip { flex-direction: column; }
          .inv-strip-item { border-right:none; border-bottom:1px solid var(--border); }
          .inv-strip-item:last-child { border-bottom:none; }
          .inv-cta { padding: 40px 20px; }
        }
        @media (min-width: 1280px) {
          .inv-hero { padding-top: 140px; }
        }
      `}</style>

      {/* ── HERO ── */}
      <section className="inv-hero">
        <div className="inv-badge fade-in">🌾 G20 MACS Endorsed · Proposed by India</div>

        <h1 className="inv-title fade-in">MAHARISHI</h1>

        <p className="inv-fullform fade-in">
          Millets and Other Ancient Grains International Research Initiative
        </p>

        <div className="inv-divider fade-in" />

        <p className="inv-tagline fade-in">
          A global platform for research, innovation, and collaboration
          in millets and ancient grains — inviting nations, institutions, and
          organisations to join as Members and shape the future of
          sustainable food systems together.
        </p>

        <div className="inv-strip fade-in">
          <div className="inv-strip-item">
            <span className="inv-strip-num">G20</span>
            <span className="inv-strip-label">Nations Backing</span>
          </div>
          <div className="inv-strip-item">
            <span className="inv-strip-num">10–12</span>
            <span className="inv-strip-label">MoUs at Launch</span>
          </div>
          <div className="inv-strip-item">
            <span className="inv-strip-num">10+</span>
            <span className="inv-strip-label">SDGs Aligned</span>
          </div>
          <div className="inv-strip-item">
            <span className="inv-strip-num">Open</span>
            <span className="inv-strip-label">Membership</span>
          </div>
        </div>

        <div className="inv-actions fade-in">
          <a href="/magic2026_registration" className="inv-btn inv-btn-primary">Express Interest to Join</a>
          <a href="/magic" className="inv-btn inv-btn-outline">Explore MAGIC 2026 →</a>
        </div>
      </section>

      <hr className="inv-hr" />

      {/* ── VISION & MISSION ── */}
      <section className="inv-section">
        <p className="inv-eyebrow fade-in">Foundation</p>
        <h2 className="inv-sec-title fade-in">Our Vision & Mission</h2>
        <p className="inv-sec-desc fade-in">
          MAHARISHI was born from a simple but powerful idea — that millets and ancient
          grains, once the backbone of civilisations, can once again anchor the world's
          food security, nutrition, and climate resilience.
        </p>

        <div className="vm-grid">
          <div className="vm-card fade-in">
            <span className="vm-card-label">Vision</span>
            <p>
              To cultivate a globally connected and forward-looking research ecosystem that unlocks the full potential of millets and ancient grains through collaborative knowledge exchange, innovation, and inclusive partnerships — ensuring sustainable agriculture, resilient food systems, and healthier communities for generations to come.
            </p>
          </div>
          <div className="vm-card fade-in">
            <span className="vm-card-label">Mission</span>
            <p>
              To establish robust mechanisms for connecting global researchers and institutions working on millets and ancient grains, enabling effective knowledge exchange, identification of research gaps, and dissemination of innovations. Through digital platforms, collaborative workshops, open-access publications, and recognition of young scientists, the initiative aims to accelerate research, build capacity, and raise awareness of the nutritional, cultural, and climate-resilient value of these crops — empowering smallholder farmers and advancing sustainable food systems.
            </p>
          </div>
        </div>
      </section>

      <hr className="inv-hr" />

      {/* ── WHY IT MATTERS ── */}
      <section className="inv-section">
        <p className="inv-eyebrow fade-in">Why It Matters</p>
        <h2 className="inv-sec-title fade-in">Millets Are a Global Imperative</h2>
        <p className="inv-sec-desc fade-in">
          As climate change threatens major food crops, millets and ancient grains —
          drought-tolerant, nutrient-rich, and water-efficient — offer a proven,
          scalable solution. They are directly linked to the achievement of 10 UN SDGs.
        </p>

        <div className="sdg-row fade-in">
          {[
            "SDG 1 – No Poverty", "SDG 2 – Zero Hunger",
            "SDG 3 – Good Health", "SDG 5 – Gender Equality",
            "SDG 6 – Clean Water", "SDG 8 – Decent Work",
            "SDG 12 – Responsible Consumption", "SDG 13 – Climate Action",
            "SDG 15 – Life on Land", "SDG 17 – Partnerships",
          ].map((s) => (
            <span className="sdg-pill" key={s}>🌱 {s}</span>
          ))}
        </div>

        <p className="matter-desc fade-in">
          In Africa and Asia, over 70% of sorghum and 95% of millets are consumed
          as staple foods. Honoured as <em style={{color:"#f0e0a0"}}>&#39;Shree-anna&#39;</em> (divine food)
          in the Indian Parliament in 2023, millets represent not just a crop — but a
          movement toward food sovereignty, cultural pride, and planetary health.
        </p>
      </section>

      <hr className="inv-hr" />

      {/* ── CTA ── */}
      <div className="inv-cta-wrap">
        <div className="inv-cta fade-in">
          <h2>Join the Magic 2026</h2>
          <p>
            Become a founding partner of MAHARISHI and help shape the world's first
            permanent multilateral research initiative for millets and ancient grains.
            Sign your MoU at MAGIC 2026 in Hyderabad.
          </p>
          
          <p className="inv-footnote">
            <br />Secretariat hosted at <strong style={{color:"#e8f0e8"}}>ICAR-IIMR, Hyderabad</strong> under India's G20 Presidency mandate.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MaharishiInvite;